import firebase from '..';
import type {
  CollectionReference,
  DocumentData,
  DocumentReference,
  PartialWithFieldValue,
  Query,
  QueryConstraint,
  SetOptions,
  Transaction,
  WithFieldValue
} from 'firebase/firestore/lite';

const SILKTIDE_ANALYTICS_KEY = 'silktideCookieChoice_analytics';

export const loadFirestore = async () => {
  const { getFirestore } = await import('firebase/firestore/lite');
  return getFirestore(firebase);
};

export async function createDocRef(path: string, ...pathSegments: string[]) {
  const firestore = await loadFirestore();
  const { doc } = await import('firebase/firestore/lite');
  return doc(firestore, path, ...pathSegments);
}

export async function createCollectionRef(
  path: string,
  ...pathSegments: string[]
) {
  const firestore = await loadFirestore();
  const { collection } = await import('firebase/firestore/lite');
  return collection(firestore, path, ...pathSegments);
}

export async function setDocument(
  reference: DocumentReference<DocumentData>,
  data: PartialWithFieldValue<DocumentData>,
  options?: SetOptions
) {
  const { setDoc } = await import('firebase/firestore/lite');
  if (options == null) return setDoc(reference, data);
  return setDoc(reference, data, options);
}

export async function getDocument(reference: DocumentReference<DocumentData>) {
  const { getDoc } = await import('firebase/firestore/lite');
  return getDoc(reference);
}

export async function getDocuments(
  queryRef: Query<DocumentData> | Promise<Query<DocumentData>>,
  ...queryConstraints: QueryConstraint[]
) {
  const { query, getDocs } = await import('firebase/firestore/lite');
  const q = query(await queryRef, ...queryConstraints);
  return getDocs(q);
}

export async function firestoreRunTransaction(
  updateFunction: (transaction: Transaction) => Promise<void>
) {
  const { runTransaction } = await import('firebase/firestore/lite');
  const firestore = await loadFirestore();
  return runTransaction(firestore, updateFunction);
}
// create a document. Cloud Firestore auto-generate an ID for you. You can do this by calling addDoc()
export async function addDocument(
  reference:
    | CollectionReference<DocumentData>
    | Promise<CollectionReference<DocumentData>>,
  data: WithFieldValue<DocumentData>
) {
  const { addDoc } = await import('firebase/firestore/lite');
  return addDoc(await reference, data);
}

export const isAnalyticsConsented = () => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return false;
  }

  try {
    const value = localStorage.getItem(SILKTIDE_ANALYTICS_KEY);
    return value === 'true';
  } catch {
    return false;
  }
}
